import { CancelOutlined, CheckCircleOutline, HighlightOff, PauseCircleOutline } from '@mui/icons-material'
import { Chip, Stack, Tooltip } from '@mui/material'
import isNil from 'lodash/isNil'
import uniqBy from 'lodash/uniqBy'
import { useTranslation } from 'react-i18next'
import { AssigneeEntity } from '@omnea-app/core/blocks'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { CounterChip } from '@omnea-app/fe-legacy/components/shared/ui/chip/CounterChip'
import { CountBadge } from '@omnea-app/omui/CountBadge'
import { getIsRenewalCancelled } from '@omnea-app/packages/universal/requests/getIsRenewalCancelled'

export const RequestStage = ({
  row,
  stageValue,
}: {
  row: {
    totalStages?: number
    completedStages?: number
    stage?: string[]
    rejected?: boolean
    paused?: boolean
    pausedAt?: string | null
    assignees?: AssigneeEntity[]
    inProgressStepCompletionDate?: Date | null
    completedAt?: string | null
    isUserAssigned?: boolean
    shouldRenew?: boolean | null
  }
  stageValue: string
}) => {
  const { t } = useTranslation('requests')
  const { formatDate } = useFormatter()
  const currentSteps = row.stage || []

  const isCompleted = row.completedStages === row.totalStages

  if (currentSteps.length === 0 && !isCompleted && !row.paused) {
    return null
  }
  const progressText = `${row.completedStages}/${row.totalStages}`
  const otherItems = currentSteps.slice(1)
  const text = stageValue
  const currentAssignees = row.assignees ? uniqBy(row.assignees, 'id') : []
  const inProgressStepCompletionDate = row.inProgressStepCompletionDate
    ? formatDate(row.inProgressStepCompletionDate, { format: 'short' })
    : ''

  const getTooltipLabel = () => {
    if (row.paused && row.pausedAt) {
      return t('stage.tooltip-paused', {
        pausedAt: formatDate(new Date(row.pausedAt), { format: 'short', withPreposition: true }),
      })
    }

    if (isCompleted) {
      const completedAtDate =
        isCompleted && row.completedAt ? formatDate(new Date(row.completedAt), { format: 'short', withPreposition: true }) : ''
      return t('stage.tooltip-completed', {
        completedAt: completedAtDate,
      })
    }

    if (isNil(inProgressStepCompletionDate) || inProgressStepCompletionDate === '' || !row.completedStages) {
      return ''
    }

    const dueDateLabel =
      inProgressStepCompletionDate === 'now'
        ? t('stage.tooltip-overdue')
        : t('stage.tooltip-due-on', { dueOn: inProgressStepCompletionDate })

    if (currentAssignees.length === 0) {
      return `${dueDateLabel}`
    } else {
      const currentAssigneesLabel = currentAssignees.length > 0 ? `${[...currentAssignees.map((i) => i.value)].join(', ')},` : ''
      const assigneeLabel = currentAssignees.length > 1 ? t('stage.tooltip-assignees') : t('stage.tooltip-assignee')
      return `${assigneeLabel} ${currentAssigneesLabel} ${dueDateLabel}`
    }
  }

  if (row.rejected) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip title={t('rejection.rejected')}>
          <Chip label={text} color="error" icon={<HighlightOff />} />
        </Tooltip>
      </Stack>
    )
  }

  const isRenewalCancelled = getIsRenewalCancelled({ isCompleted, shouldRenew: row.shouldRenew })

  const stageColor = (() => {
    if (row.paused) {
      return 'warning'
    }
    if (isCompleted) {
      return isRenewalCancelled ? 'neutral' : 'success'
    }
    return row.isUserAssigned ? 'warning' : 'info'
  })()

  const getIcon = () => {
    if (row.paused) {
      return <PauseCircleOutline />
    }
    if (isCompleted) {
      return isRenewalCancelled ? <CancelOutlined /> : <CheckCircleOutline />
    }

    return <CountBadge count={progressText} color={stageColor} />
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Tooltip title={getTooltipLabel()}>
        <Chip label={text} color={stageColor} icon={getIcon()} />
      </Tooltip>
      <CounterChip count={otherItems.length} hint={otherItems.join(', ')} />
    </Stack>
  )
}
